import { ContentSection } from "../components/sections/SimpleContent"
import {
  Image,
  ProductInterface as Props,
  SEO,
  PageSection,
  CartProduct,
  Price,
} from "../ts/interfaces"
import { isCanada, isUSA } from "../utils/Countries"
import { cleanSlug } from "../utils/format"
import { getCartProduct, getStripeId } from "../utils/stripejs"
import { isHeadset } from "./shared/calcStateItems"
import { Currency } from "./types/checkoutTypes"

export class Product {
  id: string
  stripeId?: string
  stripeTestId?: string
  slug: string
  seo?: SEO
  name: string
  featuredImage: Image
  gallery: Image[]
  sections: PageSection[]
  subHeading: string
  description: string
  shortDescription: string

  CAD_List_Price: number
  CAD_Discount: number
  USD_List_Price: number
  USD_Discount: number
  priceHeading?: string
  outOfStock?: boolean
  postPurchaseContent?: ContentSection

  relatedProductPriceLink?: string
  relatedProducts?: {
    id: string
  }[]
  canUsOnly?: boolean
  noCoupons?: boolean
  isPro?: boolean
  relatedPriceTxt: string = ""

  stripeProduct: CartProduct | null
  actualstripeId?: string

  productBundles?: {
    product: {
      id: string
    }
    forced: boolean
    title?: string
    description?: string
    skipText?: string
  }[]

  constructor(props: Props) {
    this.id = props.id
    this.stripeId = props.stripeId
    this.stripeTestId = props.stripeTestId
    this.slug = props.slug
    this.seo = props.seo
    this.name = props.name
    this.featuredImage = props.featuredImage
    this.gallery = props.gallery
    this.sections = props.sections
    this.subHeading = props.subHeading
    this.description = props.description
    this.shortDescription = props.shortDescription
    this.CAD_List_Price = props.CAD_List_Price
    this.USD_List_Price = props.USD_List_Price
    this.CAD_Discount = props.CAD_Discount
    this.USD_Discount = props.USD_Discount
    this.priceHeading = props.priceHeading
    this.outOfStock = props.outOfStock
    this.postPurchaseContent = props.postPurchaseContent
    this.relatedProductPriceLink = props.relatedProductPriceLink
    this.relatedProducts = props.relatedProducts
    this.canUsOnly = props.canUsOnly
    this.noCoupons = props.noCoupons
    this.isPro = props.isPro
    const stripeId = getStripeId(props.stripeId, props.stripeTestId)
    const stripeProduct = getCartProduct(stripeId)

    if (stripeProduct) {
      props.CAD_List_Price =
        (stripeProduct?.prices?.cad?.unit_amount ?? 0) + props.CAD_Discount
      props.USD_List_Price =
        (stripeProduct?.prices?.usd?.unit_amount ?? 0) + props.USD_Discount
    } else {
      props.CAD_List_Price = props.CAD_Discount
      props.USD_List_Price = props.USD_Discount
    }
    this.stripeProduct = stripeProduct
    this.actualstripeId = stripeId
    this.productBundles = props.productBundles
  }

  isRecurring(): boolean {
    return this.stripeProduct?.prices?.cad?.type === "recurring"
  }

  isPersonalMembership(): boolean {
    return (
      this.isRecurring() &&
      ["MEMBERSHIP_YEARLY", "MEMBERSHIP_MONTHLY"].includes(
        this.stripeProduct?.metadata.SKU ?? ""
      )
    )
  }
  isProfessionalMembership(): boolean {
    return this.isRecurring() && !this.isPersonalMembership()
  }

  isMonthly(): boolean {
    return this.stripeProduct?.prices?.cad?.recurring?.interval === "month"
  }

  getPeriodText(opts: { num: number; titleCase: boolean }): string {
    if (!this.isRecurring()) {
      return ""
    }
    let text = this.isMonthly() ? `${opts.num} Month` : `${opts.num} Year`
    if (opts.num > 1) {
      text += "s"
    }
    return opts.titleCase ? text : text.toLowerCase()
  }

  getProductName(opts: {
    headsetInCart: boolean
    couponDiscountDays: number
    trialDays: number
  }): string {
    if (this.isUsage()) {
      return this.name
    }

    const bundledMembership =
      this.isSubscriptionWithTrialDays() && opts.headsetInCart
    if (
      this.isRecurring() &&
      (bundledMembership || opts.couponDiscountDays > 0)
    ) {
      return `${Math.max(
        this.isSubscriptionWithTrialDays() ? opts.trialDays : 0,
        opts.couponDiscountDays
      )} day ${bundledMembership ? "membership included" : "trial membership"}`
    }
    return this.name
  }

  replaceUsageDescription(str: string, currency: Currency): string {
    const amt = this.getPriceAmt(currency)
    if (amt === null) {
      return str
    }
    if (this.isUsage()) {
      return str.replace("{PRICE}", `$${Math.round(amt / 100)}`).replace(
        "{UNLIMITED_PERIOD}",
        this.getPeriodText({
          num: this.getUnlimitedNumCycles(),
          titleCase: false,
        })
      )
    }
    return str
  }
  getProductBillingDescription(opts: {
    fullDescription: boolean
    currency: Currency
    trialDays: number
    couponDiscountDays: number
  }): string {
    const amt = this.getPriceAmt(opts.currency)
    if (amt === null) {
      return ""
    }
    if (this.isUsage()) {
      return this.replaceUsageDescription(this.subHeading, opts.currency)
    }

    if (this.hasUpfrontNumCycles()) {
      const numCycle = this.getUpfrontNumCycles()
      const mobilePre = opts.fullDescription
        ? `${this.getPeriodText({
            num: numCycle,
            titleCase: false,
          })} today then `
        : ""

      const billing = this.isYearly()
        ? `$${Math.round(amt / 100 / 12)} per month, billed yearly`
        : `$${Math.round(amt / 100)} per month`

      const post = !opts.fullDescription
        ? ` after ${numCycle > 1 ? `` : "first "}${this.getPeriodText({
            num: numCycle,
            titleCase: false,
          })}`
        : ""
      return mobilePre + billing + post
    }
    if (this.isRecurring()) {
      const billing = this.isYearly()
        ? `$${Math.round(amt / 100 / 12)} per month, billed yearly`
        : `$${Math.round(amt / 100)} per month`

      return `*Cancel anytime - ${billing} after ${Math.max(
        this.isSubscriptionWithTrialDays() ? opts.trialDays : 0,
        opts.couponDiscountDays
      )} days`
    }

    return ""
  }

  isYearly(): boolean {
    return this.stripeProduct?.prices?.cad?.recurring?.interval === "year"
  }

  isUsage(): boolean {
    return this.stripeProduct?.prices?.cad?.recurring?.usage_type === "metered"
  }

  isSubscriptionWithTrialDays(): boolean {
    return this.isRecurring() && !!this.stripeProduct?.metadata?.trial_days
  }

  getUpfrontNumCycles(): number {
    if (!this.isRecurring()) {
      return 1
    }

    const numCycleStr = this.stripeProduct?.metadata?.NUM_PERIOD_UPFRONT ?? "1"
    return parseInt(numCycleStr, 10)
  }

  hasUpfrontNumCycles(): boolean {
    return this.getUpfrontNumCycles() > 1
  }

  getUnlimitedNumCycles(): number {
    if (!this.isUsage()) {
      return 0
    }

    const numCycleStr = this.stripeProduct?.metadata?.NUM_PERIOD_NO_LIMIT ?? "0"
    return parseInt(numCycleStr, 10)
  }

  hasForcedBundle(): boolean {
    return this.productBundles?.some(pb => pb.forced) ?? false
  }

  getForcedProductBundleId(): string | undefined {
    const forced = this.productBundles?.find(pb => pb.forced)
    if (!forced) {
      return
    }
    return forced.product.id
  }

  getDiscount(currency: Currency): number {
    let discount = this.USD_Discount

    if (currency === "cad") {
      discount = this.CAD_Discount
    }
    return discount
  }

  getPriceAmt(
    currency: Currency,
    excludeDiscount = true,
    withUpfront = false
  ): number | null {
    const price = this.getPrice(currency)
    if (!price) {
      return null
    }

    const amt = excludeDiscount
      ? price.unit_amount + this.getDiscount(currency)
      : price.unit_amount

    return withUpfront ? amt * this.getUpfrontNumCycles() : amt
  }

  getPrice(currency: Currency): Price | null {
    if (!this.stripeProduct) {
      return null
    }
    return this.stripeProduct.prices[currency] ?? null
  }

  isHeadset(): boolean {
    return isHeadset(this.stripeProduct)
  }

  isOutOfRegion(country?: string): boolean {
    if (!country) {
      return false
    }

    return this.canUsOnly === true && !isCanada(country) && !isUSA(country)
  }

  isNoCoupon(): boolean {
    return this.noCoupons === true
  }

  setRelatedPriceText(text: string) {
    this.relatedPriceTxt = text
  }

  getRelatedProduct(
    allProducts: Product[],
    country: string
  ): Product | undefined {
    const hasRelatedPrice =
      this.relatedProductPriceLink && (this.relatedProducts?.length ?? 0) > 0

    const relProd = hasRelatedPrice
      ? allProducts.find(p => p.id === this.relatedProducts![0].id)
      : undefined
    relProd?.setRelatedPriceText(this.relatedProductPriceLink!)
    return relProd?.isOutOfRegion(country) === false ? relProd : undefined
  }

  cleanSlug(): string {
    return cleanSlug(this.slug)
  }
}
